import React from "react";
import { createBrowserRouter, RouterProvider, Navigate } from "react-router";
import {
  Home,
  Login,
  InternalUserLogin,
  ForgotPassword,
  Otp,
  VerificationOtp,
  ResetPassword,
  Layout,
  StandardQuote,
  ViewQuote,
  ReviewQuote,
  UserManagement,
  FamsLogin,
  ViewQuoteScreen,
  SyncData,
  ChangePassword,
  InternalUserManagement,
  ReleaseNotes,
  Support,
  Overview,
  ContainerOrder,
  Settings
} from 'modules'
import PrivateRoute from "PrivateRoutes";
import { OidcProvider } from '@axa-fr/react-oidc';
import { PriceListCatalogue } from "modules/quote/catelogue";
import { CompareTest } from "modules/quote/catelogue/CompareTest";
import { ReleaseNotesHistory } from "modules/admin/ReleaseNotesHistory";
import { OrderHistory } from "modules/quote/module/BottleOrder/OrderHistory";
import BottleOrderPage from "modules/quote/module/BottleOrder/BottleOrderPage";
import NewClientRequest from "modules/quote/module/NewClientRequest/NewClientRequest";
import { ReviewConfirmation } from "modules/quote/module/NewClientRequest/ReviewConfirmation";
import { CartProvider } from "./CartContext";
import { Analytics } from "modules/quote/module/Analytics/Analytics";
import { Dashboard } from "modules/quote/Dashboard/Dashboard";
import { AccountLogin } from "modules/auth/_External/Account_Registration/AccountLogin";
import { InstructionPage } from "modules/auth/_External/Account_Registration/InstructionPage";
import { VerificationClientOtp } from "modules/auth/_External/Account_Registration/VerificationClientOtp";
import { NewAccountRegistration } from "modules/auth/NewClientRegistration/NewAccountRegistration";
import { SuccessPage } from "modules/auth/NewClientRegistration/steps/SuccessPage";
import InventoryList from "modules/quote/module/BottleOrder/Inventory/InventoryList";
import PackageVersionDashboard from "modules/moniter/PackageVersionDashboard";
import { ApiErrorSnackbar } from 'components';
import ManageProfile from "modules/profiles/Manage_Profile/ManageProfile";
import { ContainerCatalogue } from "modules/container_catalogue/containerCatalogue";
import { V2Quote } from "modules/quote/Quote/V2Quote";
import ErrorBoundary from "./errors/ErrorBoundary";
import Error from './errors/Error';
import oidcProdConfig from "./config_files/oidcProdConfig";
import oidcDevConfig from "./config_files/oidcDevConfig";

const origin = window.location.origin;

const oidcConfig = origin.includes("elvisconnect.eurofinsanz.com")
  ? oidcProdConfig
  : oidcDevConfig;


const hasPermission = (token: string | null) => {
  if (!token) {
    return false;
  }

  try {
    const payload = JSON.parse(atob(token.split('.')[1] || ''));

    // External users are not subject to the Permission requirement
    if (payload?.UserType === 'External') return true;

    return payload?.Permission &&
      (
        Array.isArray(payload.Permission)
          ? payload.Permission.length > 0
          : true
      );

  } catch {
    return false;
  }
};

const isOnboardingToken = (token: string | null) => {
  if (!token) {
    return false;
  }

  try {
    const payload = JSON.parse(atob(token.split('.')[1] || ''));
    return payload?.OnboardRequest === 'True';
  } catch {
    return false;
  }
};

const isMainAppToken = (token: string | null) => {
  if (!token) {
    return false;
  }

  try {
    const payload = JSON.parse(atob(token.split('.')[1] || ''));
    return payload?.ValidOTP === 'True' && payload?.OnboardRequest !== 'True';
  } catch {
    return false;
  }
};

const getCurrentUserToken = () => {
  const token = localStorage.getItem('token');

  if (window.location.pathname === '/' && isOnboardingToken(token)) {
    localStorage.removeItem('token');
    return null;
  }

  const currentToken = localStorage.getItem('token');
  return isMainAppToken(currentToken) ? currentToken : null;
};

// Re-derives auth state on every render (route re-match, soft `navigate('/')`, etc.)
// instead of being baked once into the router config — otherwise a soft navigation
// after logout keeps showing the element resolved at the router's original build time.
const RootRoute = () => {
  const user = getCurrentUserToken();
  if (!user) return <Login />;
  return hasPermission(user) ? <Layout /> : <Navigate to="/error" replace />;
};

export default function App() {
  /** Wraps a route element in a scoped ErrorBoundary so one page crash never breaks the whole app. */
  const page = (element: React.ReactElement) => (
    <ErrorBoundary
      fallback={
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: '32px', fontFamily: 'Libre Franklin, sans-serif', textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>⚠️</div>
          <p style={{ fontSize: '14px', fontWeight: 600, color: '#003883', marginBottom: '8px' }}>This page encountered an error.</p>
          <p style={{ fontSize: '12px', color: '#555', marginBottom: '16px' }}>Please refresh or navigate to another page.</p>
          <button onClick={() => window.location.reload()} style={{ padding: '8px 24px', borderRadius: '30px', border: 'none', background: '#EE7D11', color: '#fff', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>
            Refresh
          </button>
        </div>
      }
    >
      {element}
    </ErrorBoundary>
  );

  const routes = createBrowserRouter([
    {
      path: '/',
      index: true,
      element: <RootRoute />,
      errorElement: <Error />,
    },
    { path: 'error', element: <Error permissionError /> },
    { path: 'internalUser', element: page(<InternalUserLogin />) },
    { path: 'forgotpassword', element: page(<ForgotPassword />) },
    { path: 'otp', element: page(<Otp />) },
    { path: 'verificationOtp', element: page(<VerificationOtp />) },
    { path: 'resetpassword', element: page(<ResetPassword />) },
    { path: 'famslogin', element: page(<FamsLogin />) },
    { path: 'account-registration', element: page(<AccountLogin />) },
    { path: 'instructions', element: page(<InstructionPage />) },
    { path: 'verification-client-otp', element: page(<VerificationClientOtp />) },
    { path: 'new-client-registration', element: page(<NewAccountRegistration />) },
    { path: 'registration-success', element: page(<SuccessPage />) },
    { path: 'container-catalogue', element: page(<ContainerCatalogue />) },
    {
      path: 'home',
      element: <PrivateRoute><Layout /></PrivateRoute>,
      children: [
        { index: true, element: page(<Home />) },
        { path: 'dashboard', element: page(<Dashboard />) },
        { path: 'monitor', element: page(<PackageVersionDashboard />) },
        { path: 'admin', element: page(<UserManagement />) },
        { path: 'generateQuote', element: page(<StandardQuote />) },
        { path: 'viewQuote', element: page(<ViewQuote />) },
        { path: 'reviewQuote', element: page(<ReviewQuote />) },
        { path: 'clientView', element: page(<ViewQuoteScreen />) },
        { path: 'syncData', element: page(<SyncData />) },
        { path: 'changePassword', element: page(<ChangePassword />) },
        { path: 'internal', element: page(<InternalUserManagement />) },
        { path: 'priceList', element: page(<PriceListCatalogue />) },
        { path: 'compareTest', element: page(<CompareTest />) },
        { path: 'releaseNotes', element: page(<ReleaseNotes />) },
        { path: 'releaseNotesHistory', element: page(<ReleaseNotesHistory />) },
        {
          path: 'support',
          children: [
            { index: true, element: page(<Support />) },
            { path: 'overview', element: page(<Overview />) },
            { path: 'container-ordering', element: page(<ContainerOrder />) },
          ]
        },
        { path: 'orderHistory', element: page(<OrderHistory />) },
        { path: 'bottleOrderList', element: page(<BottleOrderPage />) },
        { path: 'settings', element: page(<Settings />) },
        { path: 'analytics', element: page(<Analytics />) },
        { path: 'new-client-request', element: page(<NewClientRequest />) },
        { path: 'review-client-request', element: page(<ReviewConfirmation />) },
        { path: 'inventory', element: page(<InventoryList />) },
        { path: 'manage-profiles', element: page(<ManageProfile />) },
        { path: 'v2-quote', element: page(<V2Quote />) },
      ]
    },
    { path: '*', element: <Error notFound /> },
  ]);

  return (
    <ErrorBoundary>
      <OidcProvider configuration={oidcConfig}>
        <CartProvider>
          <RouterProvider router={routes} />
          <ApiErrorSnackbar />
        </CartProvider>
      </OidcProvider>
    </ErrorBoundary>
  );
}
